import React, { useState } from 'react';
import {
  Button,
  Column,
  Copy,
  Heading,
  Row,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { Square } from '@grubhubprod/gh-cookbook-react/dist';
import { useHistory } from 'react-router-dom';
import { EditGroupForm } from 'src/components/People/Groups/EditGroupForm';
import { IGroup } from 'src/utilities/useSearchHooks';
import { useRemoveGroup } from 'src/components/People/Groups/hooks/useRemoveGroup';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { GroupManagementModal } from './GroupManagementModal';

type GroupDetailsCardProps = {
  corpId: string;
  groupData: IGroup;
  onEditSuccess: () => void;
};
export const GroupDetailsCard: React.FC<GroupDetailsCardProps> = ({
  corpId,
  groupData,
  onEditSuccess,
}) => {
  const { isPhone } = useScreenSizes();
  const { push } = useHistory();

  const handleRemoveGroupSuccess = () => {
    push(`/corporate/management/${corpId}/people?tab=groups`);
  };

  const {
    removeGroupModalStatus,
    groupIdDeletionInProgress,
    removeGroup,
    onRemoveGroup,
    closeRemoveGroupModal,
  } = useRemoveGroup({
    corpId,
    onSuccess: handleRemoveGroupSuccess,
  });

  // TODO: implement useGetNumberOfLOCs() hook
  const numberOfLOCs = 0;
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const handleGroupEdit = () => {
    setIsEditModalVisible(true);
  };

  const handleCloseGroupEditModal = () => setIsEditModalVisible(false);

  return (
    <>
      <Square
        inset={isPhone ? 'md' : 'lg'}
        dimension="2"
        bg="untintedBackground"
        borderRadius="4px"
      >
        <Row justifyContent="space-between">
          <Column desktop={10} tablet={8}>
            <Stack flex={1}>
              <Stack y="x-sm">
                <Heading variant="h1">{groupData.name}</Heading>
              </Stack>
              <Stack y="sm">
                <Copy variant="body">{groupData.description}</Copy>
              </Stack>
              {!!numberOfLOCs && (
                <Stack>
                  <Copy
                    variant="body"
                    color="secondaryText"
                  >{`Assigned ${numberOfLOCs} Lines of credit`}</Copy>
                </Stack>
              )}
            </Stack>
          </Column>
          <Column desktop={2} tablet={4}>
            <Button
              block
              variant="secondary"
              buttonSize="large"
              onClick={handleGroupEdit}
            >
              Edit
            </Button>
            <Button
              block
              variant="muted"
              buttonSize="large"
              onClick={() => onRemoveGroup(groupData)}
            >
              Delete
            </Button>
          </Column>
        </Row>
      </Square>

      <GroupManagementModal
        isVisible={isEditModalVisible}
        onClose={handleCloseGroupEditModal}
        title="Edit group"
      >
        <EditGroupForm
          corpId={corpId}
          groupInfo={groupData}
          onSuccess={() => {
            onEditSuccess();
            handleCloseGroupEditModal();
          }}
          onCancel={handleCloseGroupEditModal}
        />
      </GroupManagementModal>
      {removeGroupModalStatus.showModal && (
        <SimpleDialog
          id={removeGroupModalStatus.groupId}
          primaryActionFlavor="danger"
          title={`Remove ${removeGroupModalStatus.groupName} group?`}
          description="Any lines of credit given to all people in this group will no longer be available."
          primaryActionText="Remove"
          isPrimaryButtonLoading={groupIdDeletionInProgress !== null}
          buttonsDirection="row-reverse"
          secondaryActionText="Cancel"
          disablePrimaryActionButton={groupIdDeletionInProgress !== null}
          disableSecondaryActionButton={groupIdDeletionInProgress !== null}
          onSecondaryActionClick={closeRemoveGroupModal}
          onPrimaryActionClick={(groupId) => removeGroup(groupId)}
        />
      )}
    </>
  );
};
